/**
 * Surface wetness (Graphics G4) — a shared TSL uniform + a material modifier.
 *
 * `applyWetness` makes a (textureless) MeshStandardMaterial respond to rain: darker
 * albedo + lower roughness (glossier reflections) as wetness rises. WeatherModule lerps
 * `setWetness` from the active WeatherProfile. For a TEXTURED surface use
 * `applyWetnessTextured` — the plain colorNode override would drop the albedo map.
 */
import * as THREE from 'three/webgpu';
import { stochasticTexture } from 'enginex/render/materials/StochasticTiling.js';
import { applyRainRipples } from 'enginex/render/materials/RainRipples.js';
import { shelterExposure } from './rain_shelter.js';
import { applySpecularAA } from 'enginex/render/materials/SpecularAA.js';
import { specularAAHandles, stage5Stats } from './stage5_fx.js';
import { rainHandles, stage4Stats } from './stage4_fx.js';
import { uniform, mix, float, color, texture, positionWorld, mx_noise_float, vec2, vec3, attribute, clamp, normalMap, normalWorld, smoothstep, normalize, length, cameraPosition, uv, time } from 'three/tsl';
import { tslHeightBlend } from 'enginex/terrain/TerrainHeightBlend.js';
import { tslTriplanarWeights, tslWhiteoutNormalBlend } from 'enginex/terrain/TerrainSampling.js';
import { tslMicroDetailNormal, tslMicroStrength } from 'enginex/terrain/TerrainMacroMicro.js';
import { Biome, BIOMES } from '@shared/terrain.js';

const wetnessUniform = uniform(0);
const snowUniform = uniform(0); // 0..1 — driven by WeatherModule while snowing
/** Blowing dust 0..1 and the wind it blows along (unit xz) — driven by WeatherModule in a sandstorm (2026-09-11). */
const dustUniform = uniform(0);
const dustWindUniform = uniform(new THREE.Vector2(1, 0));

export function applyWetness(material: THREE.MeshStandardMaterial): void {
  const m = material as unknown as { colorNode: unknown; roughnessNode: unknown; color: THREE.Color; roughness: number };
  m.colorNode = color(m.color).mul(mix(float(1.0), float(0.55), wetnessUniform)); // darken to 55%
  m.roughnessNode = float(m.roughness).mul(mix(float(1.0), float(0.3), wetnessUniform)); // glossier
}

/**
 * Wetness for a TEXTURED material (e.g. the terrain albedo). The plain `applyWetness`
 * builds its colorNode from `material.color` and IGNORES `.map` — so a textured surface
 * renders flat (bright white, when color was forced to white expecting the map to
 * colorize it). This variant samples the albedo map instead, so the texture SHOWS and
 * still darkens in rain. `texture(map)` with no uvNode auto-applies the map's
 * repeat/offset matrix (TextureNode.setUpdateMatrix), so the existing `map.repeat`
 * tiling is honored.
 */
export function applyWetnessTextured(material: THREE.MeshStandardMaterial, map: THREE.Texture): void {
  const m = material as unknown as { colorNode: unknown; roughnessNode: unknown; map: THREE.Texture; roughness: number };
  m.map = map; // keep the standard binding too (mipmaps / non-node fallback paths)
  m.colorNode = texture(map).mul(mix(float(1.0), float(0.55), wetnessUniform));
  m.roughnessNode = float(m.roughness).mul(mix(float(1.0), float(0.3), wetnessUniform));
}

/**
 * Multi-texture terrain ground (variety + anti-repetition). Blends three tiled ground
 * albedos — regolith (base), moss, and dirt — by two world-space perlin noises so the
 * surface breaks into organic patches instead of one obviously-repeating tile, plus a
 * low-frequency brightness variation. Still darkens in rain (shared wetness uniform).
 * The noises sample positionWorld so the patch layout is independent of the tile scale.
 */
export function applyTerrainGround(
  material: THREE.MeshStandardMaterial,
  regolith: THREE.Texture,
  moss: THREE.Texture,
  dirt: THREE.Texture,
): void {
  const m = material as unknown as { colorNode: unknown; roughnessNode: unknown; map: THREE.Texture; roughness: number };
  m.map = regolith;
  const a = texture(regolith), b = texture(moss), d = texture(dirt); // each tiles via its own .repeat
  const wp = positionWorld;
  // Moss/dirt patch masks from world-space noise (~50 m and ~20 m features), ramped to [0,1].
  const nMoss = mx_noise_float(vec3(wp.x.mul(0.02), wp.z.mul(0.02), 0), 0.5, 0.5);
  const nDirt = mx_noise_float(vec3(wp.x.mul(0.05).add(11.3), wp.z.mul(0.05).add(7.1), 0), 0.5, 0.5);
  const wMoss = clamp(nMoss.sub(0.50).mul(4.0), 0, 1);
  const wDirt = clamp(nDirt.sub(0.55).mul(4.0), 0, 1);
  const blended = mix(mix(a, b, wMoss), d, wDirt);
  // Mesh-matched shading (light version for personal worlds): steep eroded
  // faces shift toward desaturated exposed rock and roughen — gullies and
  // talus from the erosion bake read as stone, not draped dirt.
  const steep = smoothstep(float(0.82), float(0.55), normalWorld.y);
  const lum = blended.r.mul(0.2126).add(blended.g.mul(0.7152)).add(blended.b.mul(0.0722));
  const rockCol = vec3(lum.mul(0.62).add(0.10), lum.mul(0.60).add(0.10), lum.mul(0.58).add(0.11));
  const shaded = mix(blended.rgb, rockCol, steep.mul(0.7));
  // Large-scale (~170 m) brightness variation so even same-texture areas aren't uniform.
  const macro = mx_noise_float(vec3(wp.x.mul(0.006), wp.z.mul(0.006), 0), 0.15, 1.0); // ≈[0.85,1.15]
  m.colorNode = vec3(shaded.r, shaded.g, shaded.b).mul(macro).mul(mix(float(1.0), float(0.55), wetnessUniform));
  m.roughnessNode = float(m.roughness)
    .mul(steep.mul(0.15).add(1.0))
    .mul(mix(float(1.0), float(0.3), wetnessUniform));
}

/**
 * Personal-world ground WITH the Edit Mode paint layer: identical to
 * applyTerrainGround, but a per-vertex `paintW = [regolith, moss, dirt, mask]`
 * attribute overrides the noise patch blend where the owner painted. A
 * SEPARATE function (not a flag) so geometries without the attribute keep the
 * plain shader. WHY it must stay separate: a TSL attribute() the geometry
 * lacks is NOT a pipeline error — three warns at shader build and substitutes
 * a ZERO constant, and zero paint weights blend to black, so a shared shader
 * would silently black out every unpainted geometry.
 */
export function applyTerrainGroundPainted(
  material: THREE.MeshStandardMaterial,
  regolith: THREE.Texture,
  moss: THREE.Texture,
  dirt: THREE.Texture,
): void {
  const m = material as unknown as { colorNode: unknown; roughnessNode: unknown; map: THREE.Texture; roughness: number };
  m.map = regolith;
  const a = texture(regolith), b = texture(moss), d = texture(dirt);
  const wp = positionWorld;
  const nMoss = mx_noise_float(vec3(wp.x.mul(0.02), wp.z.mul(0.02), 0), 0.5, 0.5);
  const nDirt = mx_noise_float(vec3(wp.x.mul(0.05).add(11.3), wp.z.mul(0.05).add(7.1), 0), 0.5, 0.5);
  const wMoss = clamp(nMoss.sub(0.50).mul(4.0), 0, 1);
  const wDirt = clamp(nDirt.sub(0.55).mul(4.0), 0, 1);
  const natural = mix(mix(a, b, wMoss), d, wDirt);
  const pw = attribute('paintW', 'vec4'); // [regolith, moss, dirt, mask]
  const painted = a.mul(pw.x).add(b.mul(pw.y)).add(d.mul(pw.z));
  const blended = mix(natural, painted, pw.w);
  const steep = smoothstep(float(0.82), float(0.55), normalWorld.y);
  const lum = blended.r.mul(0.2126).add(blended.g.mul(0.7152)).add(blended.b.mul(0.0722));
  const rockCol = vec3(lum.mul(0.62).add(0.10), lum.mul(0.60).add(0.10), lum.mul(0.58).add(0.11));
  const shaded = mix(blended.rgb, rockCol, steep.mul(0.7));
  const macro = mx_noise_float(vec3(wp.x.mul(0.006), wp.z.mul(0.006), 0), 0.15, 1.0);
  m.colorNode = vec3(shaded.r, shaded.g, shaded.b).mul(macro).mul(mix(float(1.0), float(0.55), wetnessUniform));
  m.roughnessNode = float(m.roughness)
    .mul(steep.mul(0.15).add(1.0))
    .mul(mix(float(1.0), float(0.3), wetnessUniform));
}

/**
 * Biome-weighted ground shader for the Commons multi-biome terrain (Phase A).
 *
 * Reads the SIX biome weights baked per-vertex by `buildOneChunk` (via the exact JS
 * `biomeWeights(seed, wx, wz)` — same seeded hash used by foliage and the height profile)
 * from two custom vertex attributes: `biomeWA = [w0, w1, w2]` (regolith_plains, crystal_forest,
 * dune_desert) and `biomeWB = [w3, w4, w5]` (rocky_highlands, moss_wetland, fungal_basin).
 *
 * Because the weights are computed by the SAME JS function that drives the foliage and
 * terrain height, the ground color is always consistent with the plants/terrain on it.
 * City-pin collapse is already baked into the per-vertex weights — no TSL city-pin code.
 *
 * Keeps the existing macro low-frequency brightness variation and shared wetness darkening.
 *
 * @param material  A MeshStandardMaterial to wire TSL color/roughness nodes onto.
 * @param textures  One texture per biome (all six keys required, BIOMES order).
 */
export interface GroundFx {
  /** Deliot–Heitz stochastic sampling of the six near albedos (stage 4). */
  antiTiling?: boolean;
  /** Rain ripple rings on the composed normal (stage 4; the handle lands in rainHandles). */
  rainRipples?: boolean;
  /** Sample the roof map (lib/rain_shelter.ts): wetness, puddles, ripples and snow accumulation scale by the exposure (render wave 1). */
  rainShelter?: boolean;
  /** Specular AA (2026-09-15): Toksvig-filter the ground's roughness off the base biome normal map. */
  specularAA?: { varianceCap: number } | null;
}

export function applyBiomeGround(
  material: THREE.MeshStandardMaterial,
  textures: Record<Biome, THREE.Texture>,
  normals?: Partial<Record<Biome, THREE.Texture>>,
  seaLevel?: number,
  quality: 'full' | 'simple' = 'full',
  fx: GroundFx = {},
): void {
  const m = material as unknown as { colorNode: unknown; roughnessNode: unknown; normalNode: unknown; map: THREE.Texture; roughness: number };
  // Anchor .map for mipmaps / non-node fallback paths (regolith is the base biome).
  m.map = textures.regolith_plains;

  const wp = positionWorld;

  // ── Read per-vertex biome weights baked by buildOneChunk ─────────────────────
  // BIOMES order: [0] regolith_plains, [1] crystal_forest, [2] dune_desert (→ biomeWA)
  //              [3] rocky_highlands,  [4] moss_wetland,    [5] fungal_basin  (→ biomeWB)
  // The JS biomeWeights() guarantees they sum to 1 per vertex; linear interp across a
  // triangle may cause a tiny renorm drift (<0.01%) which is visually negligible.
  const wA = attribute('biomeWA', 'vec3'); // [w0, w1, w2]
  const wB = attribute('biomeWB', 'vec3'); // [w3, w4, w5]

  if (quality === 'simple') {
    // MINIMAL ground (2026-09-08): ONE albedo sample (the regolith base map)
    // tinted by the six per-vertex climate weights (each biome's mean colour),
    // the worn routes as the second and last sample, wetness as a multiply.
    // Two samplers instead of 37: no bands, no triplanar, no normals, no
    // puddles, no snow. The far tier wears the same material.
    const tint = vec3(1.0, 1.0, 1.0).mul(wA.x)        // regolith_plains — the sampled base
      .add(vec3(0.62, 0.86, 0.72).mul(wA.y))          // crystal_forest — cool green
      .add(vec3(1.18, 1.08, 0.82).mul(wA.z))          // dune_desert — warm sand
      .add(vec3(0.78, 0.78, 0.80).mul(wB.x))          // rocky_highlands — grey stone
      .add(vec3(0.60, 0.84, 0.52).mul(wB.y))          // moss_wetland — moss
      .add(vec3(0.82, 0.62, 0.86).mul(wB.z));         // fungal_basin — mauve
    const base = texture(textures[BIOMES[0]]).rgb.mul(tint);
    const roadWs = attribute('roadW', 'float');
    const road = texture(textures.rocky_highlands, uv().mul(1.7)).rgb.mul(0.8);
    const groundS = mix(base, road, roadWs);
    const macroS = mx_noise_float(vec3(wp.x.mul(0.006), wp.z.mul(0.006), 0), 0.15, 1.0);
    m.colorNode = groundS.mul(macroS).mul(mix(float(1.0), float(0.55), wetnessUniform));
    m.roughnessNode = float(m.roughness).mul(mix(float(1.0), float(0.58), wetnessUniform));
    return;
  }

  // ── Sample 6 albedo textures and blend by per-vertex biome weight ─────────────
  // `texture(map)` with no uvNode auto-applies the map's repeat/offset matrix.
  // BIOMES[i] order must match the packing order used in buildOneChunk.
  // Stage 4: with `antiTiling` each near albedo is sampled STOCHASTICALLY
  // (three jittered taps on a triangle lattice, variance-preserving blend —
  // Deliot & Heitz 2019) so the 22-repeat tiles never read as a grid; the
  // explicit uv reproduces the map's repeat the plain `texture(map)` applied.
  const near = (t: THREE.Texture) => {
    if (!fx.antiTiling) return texture(t);
    stage4Stats.stochasticGround++;
    return stochasticTexture(t, uv().mul(vec2(t.repeat.x || 1, t.repeat.y || 1)), { contrast: 1 }).node as ReturnType<typeof texture>;
  };
  let albedo = near(textures[BIOMES[0]]).mul(wA.x)     // regolith_plains
    .add(near(textures[BIOMES[1]]).mul(wA.y))           // crystal_forest
    .add(near(textures[BIOMES[2]]).mul(wA.z))           // dune_desert
    .add(near(textures[BIOMES[3]]).mul(wB.x))           // rocky_highlands
    .add(near(textures[BIOMES[4]]).mul(wB.y))           // moss_wetland
    .add(near(textures[BIOMES[5]]).mul(wB.z));          // fungal_basin

  // ── DISTANCE-BASED texture frequency blending ────────────────────────────────
  // One tile frequency can't look right both at your feet and 300 m out — the
  // 22-repeat tiles read as an obvious grid at distance. Beyond ~70 m the
  // climate blend cross-fades to a ~4.6-repeat (≈55 m tile) re-sample of the
  // SAME textures (explicit UV bypasses the repeat matrix; no new samplers).
  const camDist = length(cameraPosition.sub(wp));
  const farFade = smoothstep(float(70), float(260), camDist);
  const farUv = uv().mul(4.6);
  const albedoFar = texture(textures[BIOMES[0]], farUv).mul(wA.x)
    .add(texture(textures[BIOMES[1]], farUv).mul(wA.y))
    .add(texture(textures[BIOMES[2]], farUv).mul(wA.z))
    .add(texture(textures[BIOMES[3]], farUv).mul(wB.x))
    .add(texture(textures[BIOMES[4]], farUv).mul(wB.y))
    .add(texture(textures[BIOMES[5]], farUv).mul(wB.z));
  albedo = mix(albedo, albedoFar, farFade) as unknown as typeof albedo;

  // ── RAIN SHELTER (render wave 1, 2026-09-17): the roof map's exposure — 1 in
  //    the open, 0 under a porch, an awning, a canopy or a cave roof. The rain's
  //    every consequence on the ground scales by it; the permanent snow cap does not.
  const exposure = fx.rainShelter ? (shelterExposure(wp as never) as unknown as ReturnType<typeof float>) : float(1);
  const wetLocal = wetnessUniform.mul(exposure);
  const snowLocal = snowUniform.mul(exposure);

  // ── PUDDLE mask (doc §23): flat + depressed + rained-on → standing water.
  // Concavity proxy = low-frequency noise depressions (real curvature isn't
  // in the vertex data); gated hard on flatness so slopes shed. Fades in only
  // past moderate wetness — drizzle darkens, downpours pool.
  // 2026-09-11 ("small puddle effects when it rains"): two scales of hollows —
  // 20 m basins and 4 m pockets — so a rained-on plain shows scattered small
  // puddles, not one rare pool; they fill from moderate wetness up.
  const flatPow = clamp(normalWorld.y, 0, 1).pow(6);
  const basin = smoothstep(float(0.44), float(0.66), mx_noise_float(vec3(wp.x.mul(0.05), wp.z.mul(0.05), 0), 0.5, 0.5));
  const pocket = smoothstep(float(0.52), float(0.74), mx_noise_float(vec3(wp.x.mul(0.28).add(11.3), wp.z.mul(0.28).sub(4.7), 0), 0.5, 0.5));
  const depression = clamp(basin.mul(0.75).add(pocket.mul(0.6)), 0, 1);
  const puddle = flatPow.mul(depression).mul(smoothstep(float(0.25), float(0.7), wetLocal));

  // ── MESH-MATCHED bands (terrain-texturing goal): the surface follows the
  // GEOMETRY, not just the 270 km climate fields — beaches get sand, cliffs
  // and high ground get rock, peaks get snow, and plains break into grass
  // patches. All weights derive from positionWorld.y + normalWorld in-shader,
  // and the band palette REUSES the six bound biome textures (dune = sand,
  // rocky = stone, moss = grass) so the sampler budget is unchanged.
  const flatRaw = clamp(normalWorld.y, 0, 1);
  const steep = smoothstep(float(0.82), float(0.55), normalWorld.y);
  // Band edges wander ±~26 m at 250 m scale so they never read as contours.
  const elevJ = wp.y.add(mx_noise_float(vec3(wp.x.mul(0.004), wp.z.mul(0.004), 0), 26.0, 0.0));

  // Dry beach sand just above the waterline (the wet strip is handled below).
  const sandW = (seaLevel !== undefined
    ? float(1).sub(smoothstep(float(seaLevel + 2.0), float(seaLevel + 9.0), elevJ)).mul(0.9)
    : float(0)) as unknown as ReturnType<typeof float>;
  // Alpine exposed rock ramps in below the snowline; cliffs are rock anywhere.
  const rockHiW = smoothstep(float(240), float(320), elevJ);
  // Permanent snow cap — sheds from steep faces via the surface normal.
  const snowCapW = smoothstep(float(320), float(380), elevJ).mul(flatRaw.pow(0.7));
  const rockW = clamp(steep.mul(0.85).add(rockHiW.mul(float(1).sub(snowCapW))), 0, 1);
  // Grass patches over low, gentle plains — mid-scale noise variety so open
  // regolith country stops reading as one endless dirt tile.
  const grassNoise = clamp(
    mx_noise_float(vec3(wp.x.mul(0.012), wp.z.mul(0.012), 0), 0.5, 0.5).sub(0.42).mul(3.2), 0, 1);
  const grassW = grassNoise
    .mul(wA.x.add(wA.y).mul(0.75)) // only where plains/forest climate dominates
    .mul(float(1).sub(steep))
    .mul(float(1).sub(smoothstep(float(170), float(250), elevJ)));

  // ── Anti-flatness stack (engine terrain toolkit adoption) ────────────────────
  // 1. TRIPLANAR rock: planar XZ sampling smears cliff texture into streaks;
  //    project the SAME rock texture along X/Z on steep faces (no new
  //    samplers — same texture, extra UVs; flat ground keeps the planar path
  //    since the Y weight dominates there).
  const kTri = float(22 / 256); // match the planar repeat density (22 tiles / 256 m chunk)
  const triW = tslTriplanarWeights(float(4));
  const rockAlbedoTri = texture(textures.rocky_highlands, vec2(wp.z, wp.y).mul(kTri)).mul(triW.x)
    .add(texture(textures.rocky_highlands).mul(triW.y))
    .add(texture(textures.rocky_highlands, vec2(wp.x, wp.y).mul(kTri)).mul(triW.z));
  // 2. HEIGHT-AWARE band blending: linear mixes read as paint sprayed over
  //    paint; sharpen each transition by the layers' luminance (height proxy)
  //    so the brighter/rougher material pokes through the other (engine
  //    tslHeightBlend — returns the BASE layer's weight).
  const lumOf = (t: ReturnType<typeof texture>) =>
    t.r.mul(0.2126).add(t.g.mul(0.7152)).add(t.b.mul(0.0722));
  const baseLum = lumOf(albedo as never);
  const sharpen = (w: ReturnType<typeof float>, overlayLum: unknown) =>
    float(1).sub(tslHeightBlend(float(1).sub(w), w, baseLum, overlayLum, float(2)));
  const grassWS = sharpen(grassW as never, lumOf(texture(textures.moss_wetland) as never));
  const rockWS = sharpen(rockW as never, lumOf(rockAlbedoTri as never));
  const sandWS = sharpen(sandW as never, lumOf(texture(textures.dune_desert) as never));

  // Composite albedo: climate base → grass → rock → sand (snow lands later).
  let ground = mix(albedo, texture(textures.moss_wetland), grassWS);
  ground = mix(ground, rockAlbedoTri, rockWS);
  ground = mix(ground, texture(textures.dune_desert), sandWS);
  // Worn ROUTES (2026-08-31): the per-vertex road-surface mask paints packed
  // dirt (already a bound sampler — zero budget cost) darkened toward a used
  // track, exactly on the carriageways the settlement stamp carved.
  const roadW = attribute('roadW', 'float');
  ground = mix(ground, texture(textures.rocky_highlands, uv().mul(1.7)).mul(0.8), roadW);
  // Tilled FIELDS (Unbuilt Town wave): the fieldW channel paints worked soil
  // over the crop cells of each settlement's agricultural ring — same
  // zero-budget sampler-reuse trick as roadW, warmer and finer-grained so
  // plough soil reads apart from a trafficked track. Roads override fields.
  const fieldW = attribute('fieldW', 'float');
  ground = mix(ground, texture(textures.rocky_highlands, uv().mul(3.3)).mul(vec3(0.72, 0.58, 0.44)), fieldW.mul(roadW.oneMinus()));

  // ── Per-biome NORMAL maps (runtime-derived from the albedos) ─────────────────
  // Blend the six tangent-space normal textures by the SAME climate weights,
  // then re-mix by the SAME band weights as the albedo so bump detail follows
  // the visible surface (rock crags on cliffs, dune ripple on beaches). Snow
  // flattens the detail. normalMap's scale accepts a node, so the strength
  // itself is band-driven. (Bump mapping = this normal perturbation; specular
  // response = the roughnessNode below — full PBR surface control.)
  if (normals && BIOMES.every((b) => normals[b])) {
    let blended = texture(normals[BIOMES[0]]!).mul(wA.x)
      .add(texture(normals[BIOMES[1]]!).mul(wA.y))
      .add(texture(normals[BIOMES[2]]!).mul(wA.z))
      .add(texture(normals[BIOMES[3]]!).mul(wB.x))
      .add(texture(normals[BIOMES[4]]!).mul(wB.y))
      .add(texture(normals[BIOMES[5]]!).mul(wB.z));
    // Same SHARPENED weights as the albedo so bump detail follows the visible
    // surface; the rock normal reuses the triplanar projections.
    const rockNormalTri = texture(normals.rocky_highlands!, vec2(wp.z, wp.y).mul(kTri)).mul(triW.x)
      .add(texture(normals.rocky_highlands!).mul(triW.y))
      .add(texture(normals.rocky_highlands!, vec2(wp.x, wp.y).mul(kTri)).mul(triW.z));
    blended = mix(blended, texture(normals.moss_wetland!), grassWS) as unknown as typeof blended;
    blended = mix(blended, rockNormalTri, rockWS) as unknown as typeof blended;
    blended = mix(blended, texture(normals.dune_desert!), sandWS) as unknown as typeof blended;
    // 3. NEAR-FIELD micro detail (engine tslMicroDetailNormal, distance-faded
    //    10→55 m): procedural sub-metre grain whiteout-blended in tangent
    //    space — breaks the smooth close-up read without any extra texture.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TSL Fn tuple params type as 0-arg (same cast as the engine's own call sites)
    const microFade = (tslMicroStrength as any)(float(10), float(55));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const microUp = (tslMicroDetailNormal as any)(float(7), microFade.mul(0.85), float(0.35)); // Y-up (x, up, z) — 0.85 (was 0.5): close-up soil grain relief
    const microTangent = normalize(vec3(microUp.x, microUp.z, microUp.y));           // → tangent Z-up
    const unpacked = blended.rgb.mul(2).sub(1);
    const combined = tslWhiteoutNormalBlend(unpacked, microTangent);
    // Standing water flattens surface detail (doc §22: wetness reduces
    // porous normal detail; puddles read as a level micro-mirror).
    // 1.5 (was 0.8): the ground read as flat — rocks and soil grain need the
    // full relief ("amplify the bump mapping", 2026-08-20 field report).
    const nStrength = vec2(1.5, 1.5)
      .mul(float(1).sub(snowCapW.mul(0.7)))
      .mul(float(1).sub(puddle.mul(0.85)))
      .mul(mix(float(1.0), float(0.7), wetLocal));
    m.normalNode = normalMap(combined.mul(0.5).add(0.5), nStrength as unknown as ReturnType<typeof vec2>);
  }
  // Stage 4: raindrop rings over the composed normal — or the geometry normal
  // when no normal set is bound (up-facing only; the weather drives
  // `intensity` through rainHandles; `rainSkip` says why one declined).
  if (fx.rainRipples) {
    try { rainHandles.add(applyRainRipples(material as never, { intensity: 0, cellSize: 0.35, strength: 0.35, maskNode: fx.rainShelter ? exposure : undefined })); stage4Stats.rainMats++; }
    catch (e) { (stage4Stats as unknown as { rainSkip?: string }).rainSkip = String(e).slice(0, 160); }
  }

  // ── Macro brightness variation + wetness darkening (same as applyTerrainGround) ──
  const macro = mx_noise_float(vec3(wp.x.mul(0.006), wp.z.mul(0.006), 0), 0.15, 1.0); // ≈[0.85,1.15]
  // Mild residual cliff desaturation (the real rock TEXTURE now carries the
  // cliff look; this only adds tonal variance so faces read in flat light).
  const rockLum = ground.r.mul(0.2126).add(ground.g.mul(0.7152)).add(ground.b.mul(0.0722));
  const cliffColor = vec3(rockLum.mul(0.62).add(0.10), rockLum.mul(0.60).add(0.10), rockLum.mul(0.58).add(0.11));
  let shaded = mix(ground.rgb, cliffColor, steep.mul(0.3));
  // Snow: permanent elevation caps MERGED with weather accumulation
  // (flatness³ × snowiness) — one term, steep faces shed both. Procedural
  // snow albedo with a subtle sparkle noise (no extra sampler).
  const flatness = flatRaw.pow(3);
  const snowTotal = clamp(snowCapW.add(flatness.mul(snowLocal).mul(0.85)), 0, 1);
  const snowCol = vec3(0.90, 0.93, 0.985).mul(
    mx_noise_float(vec3(wp.x.mul(0.35), wp.z.mul(0.35), 0), 0.06, 1.0));
  shaded = mix(shaded, snowCol, snowTotal);
  // Shoreline band (terrain+water realism wave): ground near/below the sea
  // level grades into wet sand — the terrain half of the water's beach foam.
  // Purely height-driven (positionWorld.y), so it hugs every coast for free.
  const sandBand = (seaLevel !== undefined
    ? float(1).sub(smoothstep(float(seaLevel + 0.4), float(seaLevel + 3.0), wp.y))
    : float(0)) as unknown as ReturnType<typeof float>;
  if (seaLevel !== undefined) {
    shaded = mix(shaded, vec3(0.62, 0.55, 0.42), sandBand.mul(0.75));
  }
  // Puddles: darkened ground with a cool sky-ish cast (the IBL/specular does
  // the real reflecting once the roughness drops below).
  shaded = mix(shaded, shaded.mul(0.3).add(vec3(0.035, 0.05, 0.07)), puddle);
  // BLOWING SAND over the ground (a sandstorm): streaks of bright sand racing
  // downwind — anisotropic noise stretched 7:1 along the wind and scrolled at
  // ~30 m/s (Bagnold: blown sand moves within a metre of the surface, the
  // Journey / Uncharted "sand sheet" read), gated by the dust and by flatness.
  const sAlong = wp.x.mul(dustWindUniform.x).add(wp.z.mul(dustWindUniform.y));
  const sAcross = wp.x.mul(dustWindUniform.y.negate()).add(wp.z.mul(dustWindUniform.x));
  const sheetA = mx_noise_float(vec3(sAcross.mul(0.9), sAlong.mul(0.12).sub(time.mul(3.5)), 0), 0.5, 0.5);
  const sheetB = mx_noise_float(vec3(sAcross.mul(2.2).add(7.1), sAlong.mul(0.3).sub(time.mul(6.0)), 3.3), 0.5, 0.5);
  const drift = smoothstep(float(0.56), float(0.8), sheetA.mul(0.6).add(sheetB.mul(0.4))).mul(dustUniform).mul(flatRaw.pow(2));
  shaded = mix(shaded, vec3(0.86, 0.74, 0.5), drift.mul(0.6));
  m.colorNode = vec3(shaded.r, shaded.g, shaded.b).mul(macro).mul(mix(float(1.0), float(0.55), wetnessUniform));
  // Specular control (PBR roughness): micro-roughness from albedo luminance;
  // cliffs rougher, dry sand roughest, snow smoother (soft sheen), the wet
  // waterline strip glossy, and rain glosses everything.
  const lum = rockLum;
  // Gloss floors LIFTED (2026-08-20 "ground is way too shiny" report): wet
  // SOIL carries a sheen, not a mirror — the old 0.3 wet multiplier took the
  // whole landscape to roughness ≈ 0.26 in rain (and snow to 0.55). Mirror
  // gloss now belongs only to explicit PUDDLES.
  m.roughnessNode = float(m.roughness)
    .mul(lum.oneMinus().mul(0.3).add(0.8))
    .mul(steep.mul(0.15).add(1.0))
    .mul(mix(float(1.0), float(1.07), sandW))
    .mul(mix(float(1.0), float(0.74), snowTotal))
    .mul(mix(float(1.0), float(0.5), wetLocal))
    .mul(mix(float(1.0), float(0.62), sandBand))
    .mul(mix(float(1.0), float(0.1), puddle)); // puddles are a level mirror
  // Specular AA (2026-09-15): the Toksvig term reads the base biome's normal
  // map — the ground blends six and sets no `.normalMap`, so the regolith map
  // stands for the set. Composes over the roughness node built above; last.
  if (fx.specularAA) {
    try {
      specularAAHandles.add(applySpecularAA(material as never, { normalMapTexture: normals?.regolith_plains ?? null, varianceCap: fx.specularAA.varianceCap }));
      stage5Stats.specularAAMats++;
    } catch (e) { (stage5Stats as unknown as { specularAASkip?: string }).specularAASkip = String(e).slice(0, 160); }
  }
}

/** Snow accumulation layers on props/structures (engine AccumulationLayer handles) — the snow uniform drives their coverage. */
export const accumulationHandles = new Set<{ coverage: { value: number } }>();
export function setSnowiness(v: number): void { for (const h of accumulationHandles) h.coverage.value = THREE.MathUtils.clamp(v, 0, 1); (snowUniform as unknown as { value: number }).value = THREE.MathUtils.clamp(v, 0, 1); }
export function setWetness(v: number): void { (wetnessUniform as unknown as { value: number }).value = THREE.MathUtils.clamp(v, 0, 1); }
/** Blowing dust over the ground (0..1) and the wind it streams along — the sandstorm's sand sheets. */
export function setDust(v: number, windX: number, windZ: number): void {
  (dustUniform as unknown as { value: number }).value = THREE.MathUtils.clamp(v, 0, 1);
  const w = (dustWindUniform as unknown as { value: THREE.Vector2 }).value;
  const l = Math.hypot(windX, windZ) || 1; w.set(windX / l, windZ / l);
}
export function getWetness(): number { return (wetnessUniform as unknown as { value: number }).value; }
